/**
 * Component for Webhook setting page in the Dashboard.
 *
 * @component
 */

import {
  Alert,
  Button,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
  Switch,
  Table
} from 'antd';
import axios from 'axios';
import React from 'react';
import { IconTray } from '../../../../components/CustomReturnsIcons';

class WebhooksTable extends React.Component {
  constructor() {
    super();
    this.state = {
      visible: false,
      id: '',
      name: '',
      event: '',
      url: '',
      secret: '',
      active: false,
      webHooks: [],
      editWebhook: '',
      secretToken: undefined,
      closeAlert: false,
    };
  }
  formRef = React.createRef();

  componentDidMount = () => {
    this.getWebhooks();
  };

  async getWebhooks() {
    try {
      const response = await axios.get('/returns/webhook/webhooks');
      if (response.data && response.data.status === 'error') {
        const errorMessage = response.data.error;
        return message.error(errorMessage, 6);
      }
      this.setState({
        webHooks: response.data.response,
      });
    } catch (err) {
      message.error('Error retrieving your Webhooks . Try again.', 5);
    }
  }

  handleSubmit = async () => {
    try {
      const values = await this.formRef.current.validateFields();
      const trimmedValues = {
        ...values,
        name: values.name?.trim(),
        event: values.event?.trim(),
        url: values.url?.trim(),
      };
      const response = await axios.post('/returns/webhook/webhooks', {
        ...trimmedValues,
      });

      const { secret } = response.data.response;

      this.setState({
        closeAlert: true,
        secretToken: secret,
      });

      this.getWebhooks();
      return message.success('Webhook Created Successfully.', 5);
    } catch (err) {
      if (err.errorFields) {
        return message.error(
          'There is something wrong with the data you entered, Please Check again.',
          5
        );
      }

      return message.error('Error Creating Webhook. Please try again.', 5);
    }
  };

  deleteWebHook = async (id) => {
    try {
      await axios.delete(`/returns/webhook/webhooks/${id}`, {});
      this.getWebhooks();
      return message.success('Webhook Deleted Successfully.', 5);
    } catch (err) {
      return message.error('Error deleting the webhook. please try again.', 5);
    }
  };

  editWebHook = async (id) => {
    try {
      const findWebHook = this.state.webHooks.filter(
        (webhook) => webhook.id === id
      );
      const webHookData = findWebHook[0];
      this.setState({
        visible: true,
        id: webHookData.id,
        editWebhook: webHookData,
        active: webHookData.active,
      }, () => {
        this.formRef.current.setFieldsValue({
          name: webHookData.name,
          event: webHookData.event,
          url: webHookData.url,
          secret: webHookData.secret,
          active: webHookData.active,
        });
      });
    } catch (err) {
      return message.error('Error Editing the WebHook, Please try again');
    }
  };

  handleEdit = async () => {
    try {
      const values = await this.formRef.current.validateFields();
      const trimmedValues = {
        ...values,
        name: values.name?.trim(),
        event: values.event?.trim(),
        url: values.url?.trim(),
      };
      await axios.put(`/returns/webhook/webhooks/${this.state.id}`, {
        ...trimmedValues,
      });
      this.getWebhooks();
      this.setState({
        editWebhook: '',
        visible: false,
      });
      this.formRef.current.resetFields();

      return message.success('Webhook updated successfully.', 5);
    } catch (err) {
      if (err.errorFields) {
        return message.error(
          'There is something wrong with the data you entered, Please Check again.',
          5
        );
      }

      return message.error(
        'Error Updating the Webhook, Please try again.',
        5
      );
    }
  };

  showModal = () => {
    this.setState({
      visible: true,
    });
  };

  handleOk = async () => {
    if (this.state.editWebhook) {
      await this.handleEdit();
    } else {
      await this.handleSubmit();
    }
  };

  handleCancel = () => {
    this.setState({
      visible: false,
      closeAlert: false,
      secretToken: '',
      editWebhook: '',
    });
    if (this.formRef.current) {
      this.formRef.current.resetFields();
    }
  };

  confirm = () => {
    message.success('Click on Yes');
  };

  cancel = () => {
    message.error('Click on No');
  };

  toggleWebhookActive = async (id, currentActive) => {
    try {
      await axios.patch(`/returns/webhook/webhooks/${id}/status`, {
        active: !currentActive,
      });
      this.getWebhooks();
      return message.success('Webhook status updated successfully.', 5);
    } catch (err) {
      return message.error('Error updating webhook status. Please try again.', 5);
    }
  };

  render() {
    const columns = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (text) => <a>{text}</a>,
      },
      {
        title: 'Event',
        dataIndex: 'event',
        key: 'event',
      },
      {
        title: 'URL',
        dataIndex: 'url',
        key: 'url',
      },
      {
        title: 'Secret',
        dataIndex: 'secret',
        key: 'secret',
        render: (text) => {
          const last4 = text.slice(-4);
          return <span>****************{last4}</span>;
        },
      },
      {
        title: 'Enabled',
        dataIndex: 'enabled',
        key: 'enabled',
        render: (text, record) => (
          <span>
            <Switch
              size="small"
              checked={record.active}
              onChange={() => this.toggleWebhookActive(record.id, record.active)}
            />
          </span>
        ),
      },
      {
        title: 'Edit',
        dataIndex: 'Edit',
        key: 'Edit',
        render: (text, record) => (
          <span>
            <Button
              size="small"
              type="primary"
              onClick={() => this.editWebHook(record.id)}
            >
              Edit
            </Button>
          </span>
        ),
      },
      {
        title: 'Delete',
        key: 'Delete',
        render: (text, record) => (
          <Popconfirm
            title="Are you sure delete this Webhook?"
            onConfirm={() => this.deleteWebHook(record.id)}
            onCancel={this.cancel}
            okText="Yes"
            cancelText="No"
          >
            <span>
              <Button type="danger" size="small">
                Delete
              </Button>
            </span>
          </Popconfirm>
        ),
      },
    ];

    return (
      <React.Fragment>
        <div style={{ overflowX: 'auto' }}>
          <Table
            columns={columns}
            dataSource={this.state.webHooks}
            style={{ minWidth: '768px' }}
            bordered={false}
            locale={{
              emptyText: (
                <div className="flex-row flex-middle-xxs flex-center-xxs">
                  <div className="flex-col-xxs-12">
                    <IconTray size="md" />
                  </div>
                  <div>
                    <p className="TextBody TextBody--xxxs">No Data</p>
                  </div>
                </div>
              ),
            }}
          />
        </div>
        <Row type="flex" justify="end" align="top">
          <Button
            type="primary"
            onClick={() => this.showModal()}
            style={{ marginTop: '30px' }}
          >
            Create Webhook
          </Button>
        </Row>
        <Modal
          title="Create new Webhook"
          visible={this.state.visible}
          okButtonProps={
            this.state.secretToken ? { disabled: true } : { disabled: false }
          }
          onOk={this.handleOk}
          onCancel={this.handleCancel}
          width={350}
        >
          {this.state.closeAlert ? (
            <Alert
              message="Secret key created successfully"
              description={
                <React.Fragment>
                  <p>
                    Write the token down in a secure place. You will not be able
                    to access it later.
                  </p>
                  <p>API Token:{this.state.secretToken}</p>
                </React.Fragment>
              }
              type="success"
              showIcon
            />
          ) : (
            <Form className="login-form" ref={this.formRef}>
              <h4>Webhook name</h4>
              <Form.Item name="name" rules={[{ required: true, message: 'Name!' }]}>
                <Input
                  style={{ width: '100%' }}
                  placeholder="Name"
                  className="u-marginBottom--sm"
                />
              </Form.Item>
              <h4>Event</h4>
              <Form.Item name="event" rules={[{ required: true, message: 'Event!' }]}>
                <Input placeholder="Event" className="u-marginBottom--sm" />
              </Form.Item>
              <h4>URL</h4>
              <Form.Item name="url" rules={[{ required: true, message: 'URL!' }]}>
                <Input placeholder="Url" className="u-marginBottom--md" />
              </Form.Item>
              <h4>Webhook active?</h4>
              <Form.Item name="active" initialValue={this.state.active} valuePropName="checked">
                <Switch
                  checked={this.state.active}
                  onChange={() =>
                    this.setState({ active: !this.state.active })
                  }
                  size="small"
                />
              </Form.Item>
            </Form>
          )}
        </Modal>
      </React.Fragment>
    );
  }

}

export default WebhooksTable;
